import { Button } from '@react-apps/design-system';
import type { ContentLockState } from '../content-lock';
import { getApiErrorMessage } from '../display';
import { ContentNotice } from './ContentNotice';

/**
 * O que o lock de edição tem a dizer sobre um conteúdo, ou nada (`held` e
 * sem estado). O `id` é o alvo do `aria-describedby` do lápis bloqueado.
 *
 * - `locked`: `role="status"` — aparece depois do clique em editar, e só
 *   pede esperar e tentar de novo.
 * - `lost` e `error`: `role="alert"` — a pessoa precisa agir: sair da edição
 *   (o texto continua no form, para copiar) ou tentar de novo.
 *
 * O nome é texto do React, nunca HTML: veio de um perfil, não do sistema.
 */
export function EditLockNotice({
  id,
  state,
  subject,
  keepsText = true,
  retrying = false,
  onRetry,
  onExit,
}: {
  id: string;
  state: ContentLockState | undefined;
  /** Qual conteúdo, quando a tela tem vários (a grade do gabarito). */
  subject?: string;
  /** O form fica montado no `lost`: o aviso diz que o texto está lá. */
  keepsText?: boolean;
  /** O pedido do "Tentar de novo" está no ar: o botão não pede outro. */
  retrying?: boolean;
  onRetry: () => void;
  onExit: () => void;
}) {
  if (!state || state.status === 'held') return null;

  const retry = (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      disabled={retrying}
      onClick={onRetry}
    >
      {retrying ? 'Conferindo…' : 'Tentar de novo'}
    </Button>
  );

  // O lápis fica bloqueado enquanto o aviso estiver na tela: a saída para
  // quando a outra pessoa terminar é o "Tentar de novo" daqui.
  if (state.status === 'locked') {
    return (
      <ContentNotice id={id} role="status" action={retry}>
        {subject && <strong>{subject}: </strong>}
        Sendo editado por {state.lockedBy?.name ?? 'outra pessoa'}
        {state.lockedBy && formatSince(state.lockedBy.since) && (
          <>
            {' '}
            desde{' '}
            <time dateTime={state.lockedBy.since}>
              {formatSince(state.lockedBy.since)}
            </time>
          </>
        )}
        .
      </ContentNotice>
    );
  }

  if (state.status === 'lost') {
    return (
      <ContentNotice
        id={id}
        role="alert"
        action={
          <Button type="button" variant="secondary" size="sm" onClick={onExit}>
            Sair da edição
          </Button>
        }
      >
        {subject && <strong>{subject}: </strong>}
        {state.lockedBy?.name ?? 'Outra pessoa'} assumiu a edição deste
        conteúdo, e daqui nada mais é salvo.
        {keepsText && ' O texto continua no formulário para você copiar.'}
      </ContentNotice>
    );
  }

  return (
    <ContentNotice id={id} role="alert" action={retry}>
      {subject && <strong>{subject}: </strong>}
      {getApiErrorMessage(
        state.error,
        'Não foi possível conferir se alguém já está editando.',
      )}
    </ContentNotice>
  );
}

/**
 * Hora local; de outro dia, com a data junto. `null` para data que não se lê:
 * a frase perde o "desde …" em vez de mostrar "Invalid Date".
 */
function formatSince(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const sameDay = date.toDateString() === new Date().toDateString();
  return new Intl.DateTimeFormat(
    'pt-BR',
    sameDay
      ? { hour: '2-digit', minute: '2-digit' }
      : {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        },
  ).format(date);
}
